<template>
  <v-container fluid class="pa-0 settings-container">
    <v-row no-gutters class="fill-height">
      <!-- Settings Sidebar -->
      <v-col
        cols="12"
        md="3"
        lg="2"
        class="settings-sidebar"
        :class="{ 'sidebar-mobile-open': mobileNavOpen }"
      >
        <!-- Mobile Header -->
        <div class="d-md-none pa-4 d-flex align-center justify-space-between">
          <h2 class="text-h6 font-weight-medium">{{ $t('setting.name') }}</h2>
          <v-btn icon @click="mobileNavOpen = false">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </div>

        <!-- Settings Navigation -->
        <v-list nav dense class="settings-nav pa-2">
          <!-- Company Settings -->
          <v-subheader class="text-uppercase font-weight-bold caption mt-2">
            {{ $t('setting.groups.company') || 'Company' }}
          </v-subheader>

          <v-list-item
            v-can="'setting.organisation'"
            :to="{ name: 'config_org' }"
            class="mb-1"
          >
            <v-list-item-icon>
              <v-icon>mdi-office-building</v-icon>
            </v-list-item-icon>
            <v-list-item-content>
              <v-list-item-title>{{ $t('setting.tabs.organisation') || 'Organisation' }}</v-list-item-title>
            </v-list-item-content>
          </v-list-item>

          <v-list-item
            v-can="'setting.read'"
            :to="{ name: 'config_reg' }"
            class="mb-1"
          >
            <v-list-item-icon>
              <v-icon>mdi-certificate</v-icon>
            </v-list-item-icon>
            <v-list-item-content>
              <v-list-item-title>{{ $t('setting.tabs.registration') || 'Registration' }}</v-list-item-title>
            </v-list-item-content>
          </v-list-item>

          <v-list-item
            :to="{ name: 'config_logo' }"
            class="mb-1"
          >
            <v-list-item-icon>
              <v-icon>mdi-image</v-icon>
            </v-list-item-icon>
            <v-list-item-content>
              <v-list-item-title>{{ $t('setting.tabs.logo') || 'Logo' }}</v-list-item-title>
            </v-list-item-content>
          </v-list-item>

          <!-- Sales & Commerce -->
          <v-subheader class="text-uppercase font-weight-bold caption mt-4">
            {{ $t('setting.groups.sales') || 'Sales & Commerce' }}
          </v-subheader>

          <v-list-item
            v-can="'setting.receipt'"
            :to="{ name: 'receipts' }"
            class="mb-1"
          >
            <v-list-item-icon>
              <v-icon>mdi-receipt</v-icon>
            </v-list-item-icon>
            <v-list-item-content>
              <v-list-item-title>{{ $t('setting.tabs.receipts') || 'Receipts' }}</v-list-item-title>
            </v-list-item-content>
          </v-list-item>

          <v-list-item
            :to="{ name: 'print_terminals' }"
            class="mb-1"
          >
            <v-list-item-icon>
              <v-icon>mdi-printer</v-icon>
            </v-list-item-icon>
            <v-list-item-content>
              <v-list-item-title>{{ $t('setting.tabs.sales_print_terminals') || 'Print Terminals' }}</v-list-item-title>
            </v-list-item-content>
          </v-list-item>

          <v-list-item
            v-can="'setting.pos'"
            :to="{ name: 'pos_settings' }"
            class="mb-1"
          >
            <v-list-item-icon>
              <v-icon>mdi-point-of-sale</v-icon>
            </v-list-item-icon>
            <v-list-item-content>
              <v-list-item-title>{{ $t('setting.tabs.pos') || 'POS Settings' }}</v-list-item-title>
            </v-list-item-content>
          </v-list-item>

          <!-- Products & Inventory -->
          <v-subheader class="text-uppercase font-weight-bold caption mt-4">
            {{ $t('setting.groups.products') || 'Products & Inventory' }}
          </v-subheader>

          <v-list-item
            v-can="'setting.price-list'"
            :to="{ name: 'price_lists' }"
            class="mb-1"
          >
            <v-list-item-icon>
              <v-icon>mdi-currency-usd</v-icon>
            </v-list-item-icon>
            <v-list-item-content>
              <v-list-item-title>{{ $t('setting.tabs.price_lists') || 'Price Lists' }}</v-list-item-title>
            </v-list-item-content>
          </v-list-item>

          <v-list-item
            v-can="'setting.default-message'"
            :to="{ name: 'default_messages' }"
            class="mb-1"
          >
            <v-list-item-icon>
              <v-icon>mdi-message-text</v-icon>
            </v-list-item-icon>
            <v-list-item-content>
              <v-list-item-title>{{ $t('setting.tabs.default_messages') || 'Default Messages' }}</v-list-item-title>
            </v-list-item-content>
          </v-list-item>
        </v-list>
      </v-col>

      <!-- Settings Content Area -->
      <v-col
        cols="12"
        md="9"
        lg="10"
        class="settings-content"
      >
        <!-- Mobile Toggle Button -->
        <v-btn
          icon
          class="d-md-none ma-2"
          @click="mobileNavOpen = true"
          v-if="!mobileNavOpen"
        >
          <v-icon>mdi-menu</v-icon>
        </v-btn>

        <!-- Page Header -->
        <div class="settings-header pa-6 pb-4">
          <h1 class="text-h5 font-weight-medium mb-1">{{ pageTitle }}</h1>
          <p class="text-body-2 mb-0 text--secondary" v-if="pageDescription">
            {{ pageDescription }}
          </p>
        </div>

        <!-- Content Area -->
        <div class="pa-6 pt-4">
          <transition name="settings-fade" mode="out-in">
            <router-view
              @parent-title="updateTitle"
              @parent-description="updateDescription"
            />
          </transition>
        </div>
      </v-col>
    </v-row>

    <!-- Mobile Backdrop -->
    <v-overlay
      v-model="mobileNavOpen"
      class="d-md-none"
      opacity="0.46"
      @click.native="mobileNavOpen = false"
    />
  </v-container>
</template>

<script>
export default {
  name: 'Settings',
  data () {
    return {
      pageTitle: this.$t('setting.name') || 'Settings',
      pageDescription: '',
      mobileNavOpen: false
    }
  },
  methods: {
    updateTitle (title) {
      this.pageTitle = title
    },
    updateDescription (description) {
      this.pageDescription = description
    }
  },
  watch: {
    $route: {
      immediate: true,
      handler () {
        this.mobileNavOpen = false
      }
    }
  },
  mounted () {
    if (this.$route.meta && this.$route.meta.title) {
      this.pageTitle = this.$route.meta.title
    }
  }
}
</script>

<style scoped>
.settings-container {
  height: calc(100vh - 64px);
  overflow: hidden;
}

.settings-sidebar {
  height: 100%;
  overflow-y: auto;
  border-right: 1px solid transparent;
}

.theme--light .settings-sidebar {
  border-right-color: rgba(0, 0, 0, 0.12);
}

.theme--dark .settings-sidebar {
  border-right-color: rgba(255, 255, 255, 0.12);
}

.settings-content {
  height: 100%;
  overflow-y: auto;
  background: transparent;
}

.settings-header {
  background: transparent;
  border-bottom: 1px solid transparent;
}

.theme--light .settings-header {
  border-bottom-color: rgba(0, 0, 0, 0.12);
}

.theme--dark .settings-header {
  border-bottom-color: rgba(255, 255, 255, 0.12);
}

/* Mobile Sidebar */
@media (max-width: 959px) {
  .settings-sidebar {
    position: fixed;
    top: 0;
    left: -100%;
    width: 280px;
    height: 100vh;
    z-index: 6;
    transition: left 0.3s ease;
  }

  .settings-sidebar.sidebar-mobile-open {
    left: 0;
  }
}

/* Transition — opacity only, no translate */
.settings-fade-enter-active,
.settings-fade-leave-active {
  transition: opacity 0.2s ease;
}

.settings-fade-enter,
.settings-fade-leave-to {
  opacity: 0;
}
</style>
